/**
 * Template Management Effects
 * NgRx effects for admin template async operations
 */
import { Injectable, inject } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Store } from '@ngrx/store';
import { of } from 'rxjs';
import { map, catchError, switchMap, withLatestFrom } from 'rxjs/operators';
import { TemplateService } from '../../services/template.service';
import * as TemplateActions from './template.actions';
import { selectFilters, selectTemplatesPage, selectTemplatesPageSize } from './template.selectors';

@Injectable()
export class TemplateEffects {
  private actions$ = inject(Actions);
  private templateService = inject(TemplateService);
  private store = inject(Store);

  // Load Templates
  loadTemplates$ = createEffect(() =>
    this.actions$.pipe(
      ofType(TemplateActions.loadTemplates),
      withLatestFrom(
        this.store.select(selectFilters),
        this.store.select(selectTemplatesPage),
        this.store.select(selectTemplatesPageSize)
      ),
      switchMap(([action, currentFilters, currentPage, currentPageSize]) => {
        const filters = action.filters ?? currentFilters;
        const page = action.page ?? currentPage;
        const pageSize = action.pageSize ?? currentPageSize;

        return this.templateService.getTemplates(filters, page, pageSize).pipe(
          map(response => TemplateActions.loadTemplatesSuccess({ response })),
          catchError(error => of(TemplateActions.loadTemplatesFailure({
            error: error.error?.detail || 'Failed to load templates'
          })))
        );
      })
    )
  );

  // Load Template Stats
  loadTemplateStats$ = createEffect(() =>
    this.actions$.pipe(
      ofType(TemplateActions.loadTemplateStats),
      switchMap(() =>
        this.templateService.getTemplateStats().pipe(
          map(stats => TemplateActions.loadTemplateStatsSuccess({ stats })),
          catchError(error => of(TemplateActions.loadTemplateStatsFailure({
            error: error.error?.detail || 'Failed to load template stats'
          })))
        )
      )
    )
  );

  // Load Single Template
  loadTemplate$ = createEffect(() =>
    this.actions$.pipe(
      ofType(TemplateActions.loadTemplate),
      switchMap(({ templateId }) =>
        this.templateService.getTemplate(templateId).pipe(
          map(response => TemplateActions.loadTemplateSuccess({ response })),
          catchError(error => of(TemplateActions.loadTemplateFailure({
            error: error.error?.detail || 'Failed to load template'
          })))
        )
      )
    )
  );

  // Create Template
  createTemplate$ = createEffect(() =>
    this.actions$.pipe(
      ofType(TemplateActions.createTemplate),
      switchMap(({ request }) =>
        this.templateService.createTemplate(request).pipe(
          map(template => TemplateActions.createTemplateSuccess({ template })),
          catchError(error => of(TemplateActions.createTemplateFailure({
            error: error.error?.detail || 'Failed to create template'
          })))
        )
      )
    )
  );

  // Update Template
  updateTemplate$ = createEffect(() =>
    this.actions$.pipe(
      ofType(TemplateActions.updateTemplate),
      switchMap(({ templateId, request }) =>
        this.templateService.updateTemplate(templateId, request).pipe(
          map(template => TemplateActions.updateTemplateSuccess({ template })),
          catchError(error => of(TemplateActions.updateTemplateFailure({
            error: error.error?.detail || 'Failed to update template'
          })))
        )
      )
    )
  );

  // Delete Template
  deleteTemplate$ = createEffect(() =>
    this.actions$.pipe(
      ofType(TemplateActions.deleteTemplate),
      switchMap(({ templateId, hardDelete }) =>
        this.templateService.deleteTemplate(templateId, hardDelete ?? false).pipe(
          map(response => TemplateActions.deleteTemplateSuccess({
            templateId: response.template_id || templateId,
            message: response.message
          })),
          catchError(error => of(TemplateActions.deleteTemplateFailure({
            error: error.error?.detail || 'Failed to delete template'
          })))
        )
      )
    )
  );

  // Clone Template
  cloneTemplate$ = createEffect(() =>
    this.actions$.pipe(
      ofType(TemplateActions.cloneTemplate),
      switchMap(({ templateId, request }) =>
        this.templateService.cloneTemplate(templateId, request).pipe(
          map(template => TemplateActions.cloneTemplateSuccess({ template })),
          catchError(error => of(TemplateActions.cloneTemplateFailure({
            error: error.error?.detail || 'Failed to clone template'
          })))
        )
      )
    )
  );

  // Approve Template
  approveTemplate$ = createEffect(() =>
    this.actions$.pipe(
      ofType(TemplateActions.approveTemplate),
      switchMap(({ templateId, reason, aiConfig }) =>
        this.templateService.approveTemplate(templateId, reason, aiConfig).pipe(
          map(response => TemplateActions.approvalSuccess({
            template: response.template,
            message: response.message
          })),
          catchError(error => of(TemplateActions.approvalFailure({
            error: error.error?.detail || 'Failed to approve template'
          })))
        )
      )
    )
  );

  // Reject Template
  rejectTemplate$ = createEffect(() =>
    this.actions$.pipe(
      ofType(TemplateActions.rejectTemplate),
      switchMap(({ templateId, reason }) =>
        this.templateService.rejectTemplate(templateId, reason).pipe(
          map(response => TemplateActions.approvalSuccess({
            template: response.template,
            message: response.message
          })),
          catchError(error => of(TemplateActions.approvalFailure({
            error: error.error?.detail || 'Failed to reject template'
          })))
        )
      )
    )
  );

  // Validate JSON
  validateJson$ = createEffect(() =>
    this.actions$.pipe(
      ofType(TemplateActions.validateJson),
      switchMap(({ jsonContent }) =>
        this.templateService.validateJson(jsonContent).pipe(
          map(response => TemplateActions.validateJsonSuccess({ response })),
          catchError(error => of(TemplateActions.validateJsonFailure({
            error: error.error?.detail || 'Failed to validate JSON'
          })))
        )
      )
    )
  );

  // Refresh templates after successful create/clone
  refreshAfterCreate$ = createEffect(() =>
    this.actions$.pipe(
      ofType(TemplateActions.createTemplateSuccess, TemplateActions.cloneTemplateSuccess),
      map(() => TemplateActions.loadTemplateStats())
    )
  );

  // Refresh templates after successful delete
  refreshAfterDelete$ = createEffect(() =>
    this.actions$.pipe(
      ofType(TemplateActions.deleteTemplateSuccess),
      map(() => TemplateActions.loadTemplateStats())
    )
  );

  // Update templates list after approval
  refreshAfterApproval$ = createEffect(() =>
    this.actions$.pipe(
      ofType(TemplateActions.approvalSuccess),
      map(() => TemplateActions.loadTemplateStats())
    )
  );
}

